/**
 * @class
 * Model to handle attribute profile data
 * https://kb.netx.net/display/DEV/Attribute+Profiles+for+Folders
 */

import Base from '@netx/core/lib/netx-model';
import AttributeProfileAttributes from '../collections/attributeProfileAttributes';
import AttributeProfileAttribute from '../models/attributeProfileAttribute';
import {isEmptyValue} from '@netx/core/lib/underscore-netx';
import {ATTRIBUTE_MULTIPLE_VALUE_TYPES} from '@netx/core-attribute/constants';
import {assetAttributes} from '@netx/core-attribute/singletons';
import {csvToArray} from '@netx/core-next/utils/array';

export default class AttributeProfile extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @type {string}
	 * @private
	 */
	get idAttribute() {
		return 'profileId';
	}

	/**
	 * @property {object} defaults - The default attribute profile values
	 * @property {string} defaults.name - Name of attribute profile
	 * @property {object[]} defaults.items - Objects representing profile attributes and values
	 * @property {number} defaults.items[i].templateId - Attribute template id
	 * @property {string[]} defaults.items[i].values - Attribute values
	 * @property {boolean} defaults.items[i].append - Whether attribute value is to be appended or not
	 */
	get defaults() {
		return {
			name: '',
			items: [],
		};
	}

	/** @type {object} */
	get methods() {
		return {
			create(opts) {
				return ['setAttributeProfile', this.self()];
			},
			update(opts) {
				return ['setAttributeProfile', this.self()];
			},
			remove(opts) {
				return ['removeAttributeProfile', this.id];
			},
			setAttributeProfileForFolder(folderId, opts) {
				return ['setAttributeProfileForFolder', folderId, this.self()];
			},
			removeAttributeProfileFromFolder(folderId, opts) {
				return ['removeAttributeProfileFromFolder', folderId];
			},
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		this.assetAttributes = assetAttributes;

		this.attributesSelected = new AttributeProfileAttributes();
		this.addSubCollection(this.attributesSelected);

		this.populateSelectedAttributes();

		this.listenTo(
			dispatcher,
			'attribute:profile:edit:success',
			(attributeProfile) => {
				if (attributeProfile.profileId !== this.id) return;
				this.set(attributeProfile);
			},
		);
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Options
	 */
	parseAndSave(data, opts) {
		/* Structure of `data` arg
		{
		 	name: <profile name>
		 	attribute-profile-attributes-item: {
		 		<index>: <attribute id>,
		 		...
		 	}
		 	<attribute name>: <attribute value>,
		 	<attribute name>-append: <boolean>
		 	...
		}
		*/

		const attrIds = Object.values(
			data['attribute-profile-attributes-item'] || {},
		).map(Number);

		const items = attrIds
			.map((attrId) => {
				const attr = assetAttributes.get(attrId);
				if (!attr) return;

				const name = attr.get('name');
				const type = attr.get('type');

				let _data = data[name];

				return {
					templateId: attrId,
					values: ATTRIBUTE_MULTIPLE_VALUE_TYPES.includes(type)
						? csvToArray(_data)
						: Array.isArray(_data)
						? _data
						: [_data],
					appendable: data[`${name}-append`] || false,
				};
			})
			.filter((attrObj) => attrObj != null);

		return this.save({name: data.name, items}, {wait: true, ...opts}).then(
			// Repopulate our selected attributes after any changes
			this.populateSelectedAttributes,
		);
	}

	/**
	 * @method
	 * Loop through the `items` array attribute to find all template IDs
	 * for this attribute profile and get the associated models for each
	 * and place them in an AttributeProfileAttributes collection
	 */
	populateSelectedAttributes() {
		const attributeModels = this.get('items')
			.map((attr, idx) => {
				const _attrModel = this.assetAttributes.get(attr.templateId);
				if (!_attrModel) return;

				const attrModel = new AttributeProfileAttribute({
					..._attrModel.attributes,
				});
				attrModel.set({
					append: attr.appendable,
					orderby: idx,
				});

				if (!ATTRIBUTE_MULTIPLE_VALUE_TYPES.includes(attrModel.get('type'))) {
					attrModel.set('value', attr.values[0]);
				} else {
					attrModel.set('value', attr.values);
				}

				if (isEmptyValue(attrModel.get('value'))) {
					attrModel.set('cleared', true);
				}

				return attrModel;
			})
			.filter((attr) => attr != null);

		this.attributesSelected.set(attributeModels);
	}

	/** @type {string} */
	static get __name__() {
		return 'AttributeProfile';
	}
}
